import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_CONTENT = {
  open_letter: {
    eyebrow: 'Open Letter',
    headline: 'Your signature is recorded.',
    body: 'The standard exists because people commit to it.',
  },
  institutional: {
    eyebrow: 'Institutional Interest',
    headline: 'Your interest is registered.',
    body: 'The standard is built by those who support it.',
  },
  council: {
    eyebrow: 'Council Interest',
    headline: 'Your application is received.',
    body: 'The Council exists because the standard exists.',
  },
  whitepaper: {
    eyebrow: 'Whitepaper Download',
    headline: 'Your whitepaper is downloading.',
    body: 'The standard is built by those who study it. Thank you for your interest.',
  },
  default: {
    eyebrow: 'Support the Standard',
    headline: 'Independent governance requires independent support.',
    body: 'The standard is built and maintained by those who fund it.',
  },
};

const PRESET_AMOUNTS = [100, 250, 500, 1000, 5000];

const SupportPage = () => {
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source') || 'default';
  const next = searchParams.get('next') || '/';

  const content = useMemo(
    () => SOURCE_CONTENT[source] || SOURCE_CONTENT.default,
    [source]
  );

  const [interval, setInterval] = useState('monthly'); // 'monthly' | 'one_time'
  const [amount, setAmount] = useState(250);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedAmount = isCustom
    ? parseInt(customAmount, 10) || 0
    : amount;

  const validAmount = selectedAmount >= 25;

  const handleContinue = async (e) => {
    e.preventDefault();
    if (!validAmount || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const apiInterval = interval === 'monthly' ? 'monthly' : 'one_time';
      const res = await axios.post(`${API}/support/create-checkout-session`, {
        amount: selectedAmount,
        interval: apiInterval,
        source,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error('Could not start checkout. Please try again.');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Checkout could not be started. Please try again.';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="support-page" className="bg-slate-50 min-h-screen">
      {/* Contextual thank-you */}
      <section className="bg-slate-900 text-white py-16 md:py-24">
        <div className="max-w-3xl mx-auto container-padding">
          <p className="caption text-cyan-400 mb-4" data-testid="support-eyebrow">
            {content.eyebrow}
          </p>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white mb-6"
            data-testid="support-headline"
          >
            {content.headline}
          </h1>
          <p className="text-lg md:text-xl text-slate-300" data-testid="support-body">
            {content.body}
          </p>
        </div>
      </section>

      {/* Support module */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto container-padding">
          <p className="caption text-cyan-700 mb-4">Support the Standard</p>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 tracking-tight">
            Independent governance requires independent support.
          </h2>
          <div className="space-y-4 text-base text-slate-600 leading-relaxed mb-10">
            <p>
              SAFE is a privately funded initiative. It is not coupled to any
              platform, builder, or verifier — and it never will be.
            </p>
            <p>
              Independence is not a marketing claim. It is a funding model.
              Supporter contributions allow SAFE to remain free of corporate
              influence and to fund the Council, the research, and the
              infrastructure that maintains the standard.
            </p>
            <p className="text-slate-900 font-semibold">
              Help us stay independent.
            </p>
          </div>

          <form onSubmit={handleContinue} className="space-y-8" data-testid="support-form">
            {/* Interval toggle */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3">
                Frequency
              </label>
              <div className="inline-flex bg-white border border-slate-300" role="group">
                <button
                  type="button"
                  onClick={() => setInterval('monthly')}
                  className={`px-6 py-3 text-sm font-semibold tracking-wide transition-colors ${
                    interval === 'monthly'
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                  data-testid="support-interval-monthly"
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setInterval('one_time')}
                  className={`px-6 py-3 text-sm font-semibold tracking-wide transition-colors border-l border-slate-300 ${
                    interval === 'one_time'
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                  data-testid="support-interval-onetime"
                >
                  One-time
                </button>
              </div>
            </div>

            {/* Amount tiers */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3">
                Amount (USD)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {PRESET_AMOUNTS.map((preset) => (
                  <button
                    type="button"
                    key={preset}
                    onClick={() => {
                      setAmount(preset);
                      setIsCustom(false);
                      setCustomAmount('');
                    }}
                    className={`py-5 text-lg font-bold border-2 transition-colors ${
                      !isCustom && amount === preset
                        ? 'border-slate-900 bg-slate-900 text-white'
                        : 'border-slate-300 bg-white text-slate-900 hover:border-slate-500'
                    }`}
                    data-testid={`support-amount-${preset}`}
                  >
                    ${preset.toLocaleString()}
                  </button>
                ))}
              </div>

              {/* Custom amount */}
              <div className="mt-4">
                <label className="block text-sm text-slate-600 mb-2">
                  Or enter a custom amount (minimum $25)
                </label>
                <div className="relative max-w-xs">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">
                    $
                  </span>
                  <input
                    type="number"
                    min="25"
                    step="1"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setIsCustom(true);
                    }}
                    onFocus={() => setIsCustom(true)}
                    placeholder="25"
                    className={`w-full pl-8 pr-4 py-3 border-2 text-base focus:outline-none transition-colors ${
                      isCustom
                        ? 'border-slate-900'
                        : 'border-slate-300 focus:border-slate-500'
                    }`}
                    data-testid="support-amount-custom"
                  />
                </div>
                {isCustom && customAmount && parseInt(customAmount, 10) < 25 && (
                  <p className="text-xs text-red-600 mt-2" data-testid="support-amount-error">
                    Minimum support amount is $25.
                  </p>
                )}
              </div>
            </div>

            {/* Submit + skip */}
            <div className="pt-6 border-t border-slate-200">
              <button
                type="submit"
                disabled={!validAmount || isSubmitting}
                className={`btn-primary w-full md:w-auto ${
                  !validAmount || isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                data-testid="support-continue-btn"
              >
                {isSubmitting
                  ? 'Redirecting to secure checkout…'
                  : `Continue to checkout — $${selectedAmount.toLocaleString()}${interval === 'monthly' ? ' / month' : ''}`}
              </button>
              <p className="text-xs text-slate-500 mt-3">
                Secure payment processed by Stripe. SAFE is not a registered
                non-profit; contributions are not tax-deductible.
              </p>
            </div>
          </form>

          {/* Skip */}
          <div className="mt-10 pt-8 border-t border-slate-200 text-center">
            <Link
              to={next}
              className="text-sm text-slate-500 hover:text-slate-900 underline underline-offset-4"
              data-testid="support-skip-link"
            >
              No thanks, continue →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SupportPage;
